import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import { webDevelopmentFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const WebDevelopment = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Web Development Services - Custom Web Applications | OctalChip',
    description: 'Professional web development services using React, Next.js, Vue.js, and Node.js. We create responsive, scalable, and SEO-optimized websites that drive business growth.',
    keywords: 'web development, React development, Next.js, Vue.js, Node.js, custom web applications, responsive websites, SEO optimization',
    canonical: 'https://www.octalchip.com/services/web-development',
    faqData: webDevelopmentFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Web Development Services",
      "description": "Custom web applications built with modern frameworks like React, Next.js, Vue.js, and Node.js",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    {
      title: 'Responsive Design',
      description: 'Mobile-first approach ensuring your website looks perfect on all devices from smartphones to desktops',
      icon: 'Smartphone'
    },
    {
      title: 'SEO Optimization',
      description: 'Built-in SEO best practices, meta tags, and structured data to improve your search engine rankings',
      icon: 'Search'
    },
    {
      title: 'Performance Optimization',
      description: 'Lightning-fast load times with optimized code, lazy loading, and CDN integration',
      icon: 'Zap'
    },
    {
      title: 'Cross-browser Compatibility',
      description: 'Works seamlessly across all major browsers including Chrome, Firefox, Safari, and Edge',
      icon: 'Globe'
    },
    {
      title: 'Scalable Architecture',
      description: 'Built to grow with your business needs, handling increased traffic and features effortlessly',
      icon: 'TrendingUp'
    },
    {
      title: 'Security First',
      description: 'Enterprise-grade security with SSL certificates, data encryption, and regular security audits',
      icon: 'Shield'
    }
  ];

  const technologies = [
    { name: 'React', category: 'Frontend', description: 'Modern UI library for building interactive interfaces' },
    { name: 'Next.js', category: 'Framework', description: 'Production-ready React framework with SSR and SSG' },
    { name: 'Vue.js', category: 'Frontend', description: 'Progressive JavaScript framework for modern UIs' },
    { name: 'Node.js', category: 'Backend', description: 'Server-side JavaScript runtime for scalable applications' },
    { name: 'TypeScript', category: 'Language', description: 'Typed superset of JavaScript for better code quality' },
    { name: 'Angular', category: 'Framework', description: 'Platform for building mobile and desktop applications' },
    { name: 'Express.js', category: 'Backend', description: 'Fast, unopinionated web framework for Node.js' },
    { name: 'MongoDB', category: 'Database', description: 'NoSQL database for modern applications' },
    { name: 'PostgreSQL', category: 'Database', description: 'Advanced open-source relational database' }
  ];

  const useCases = [
    {
      title: 'E-commerce Platforms',
      description: 'Build robust online stores with shopping carts, payment integration, inventory management, and order tracking',
      icon: 'ShoppingCart'
    },
    {
      title: 'SaaS Applications',
      description: 'Create scalable software-as-a-service platforms with subscription management and multi-tenancy',
      icon: 'Cloud'
    },
    {
      title: 'Corporate Websites',
      description: 'Professional business websites with CMS integration, content management, and lead generation',
      icon: 'Briefcase'
    },
    {
      title: 'Web Portals',
      description: 'Custom portals for clients, employees, or partners with role-based access control',
      icon: 'Users'
    },
    {
      title: 'Progressive Web Apps',
      description: 'PWAs that work offline and provide app-like experience on mobile devices',
      icon: 'Smartphone'
    },
    {
      title: 'Dashboard & Analytics',
      description: 'Data visualization dashboards with real-time analytics, reporting, and insights',
      icon: 'BarChart'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'We analyze your requirements, target audience, and business goals to create a comprehensive project plan with timelines and milestones',
      icon: 'Search'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Our designers create wireframes, mockups, and interactive prototypes for your approval before development begins',
      icon: 'Palette'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Agile development with regular sprints, code reviews, continuous integration, and daily progress updates',
      icon: 'Code'
    },
    {
      step: '04',
      title: 'Testing & QA',
      description: 'Comprehensive testing including unit tests, integration tests, performance testing, and user acceptance testing',
      icon: 'CheckCircle'
    },
    {
      step: '05',
      title: 'Deployment',
      description: 'Smooth deployment to production with zero-downtime strategies, monitoring, and performance optimization',
      icon: 'Rocket'
    },
    {
      step: '06',
      title: 'Support & Maintenance',
      description: 'Ongoing support, updates, bug fixes, and feature enhancements to keep your site running smoothly',
      icon: 'Wrench'
    }
  ];

  const benefits = [
    'Increased online visibility and brand presence',
    'Improved user engagement and conversion rates',
    'Better search engine rankings and organic traffic',
    'Enhanced user experience across all devices',
    'Scalable infrastructure for business growth',
    'Reduced maintenance costs with modern architecture',
    'Faster page load times and better performance',
    'Secure and compliant with industry standards'
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Enhanced Hero Section */}
      <section id="hero" className="relative py-8 sm:py-12 md:py-16 lg:py-20 xl:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl sm:rounded-2xl mb-6 sm:mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="Globe" size={32} className="sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-800 mb-4 sm:mb-6 leading-tight px-4">
              Web Development
              <span className="block text-[#00d690] mt-1 sm:mt-2">Services</span>
            </h1>
            <div className="w-20 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-6 sm:mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
              Transform your digital presence with custom web applications built using cutting-edge technologies. 
              We create responsive, scalable, and high-performance websites that drive business growth.
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 px-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/services#share-your-requirements')}
                className="px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Start Your Project
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/services#services')}
                className="px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg border-2"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-6 sm:py-8 md:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">25+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">2-8</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">100%</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">24/7</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Key Features</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive features designed to deliver exceptional web experiences
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={20} className="sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#00d690] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Technologies We Master</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              We work with the latest and most powerful web technologies to build modern applications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                    {tech.name}
                  </span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">
                    {tech.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">What We Build</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              From simple websites to complex web applications, we deliver solutions for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={20} className="sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#00d690] transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Our Development Process</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              A proven methodology that ensures quality, transparency, and timely delivery
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-12">
                {processSteps.map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-6">
                    {/* Step Number */}
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                          <Icon name={step.icon} size={24} className="text-[#00d690]" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Why Choose Our Web Development Services?</h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 bg-white/5 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={14} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <p className="text-sm sm:text-base md:text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
              Ready to Build Your Web Application?
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
              Let's discuss your project requirements and create a solution that drives your business forward. 
              Get a free consultation and quote today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/services#services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 text-sm sm:text-base"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={webDevelopmentFAQs}
        title="Web Development FAQs"
        description="Common questions about our web development services, technologies, pricing, and process."
      />

      <Footer />
    </div>
  );
};

export default WebDevelopment;
